import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  getPrintifyProduct,
  orderPrintifyProductImages,
  type PrintifyProductDetail,
} from '@/lib/printify/products'

/**
 * Read-only Printify product detail (worker proxies API).
 */
export default function PrintifyProductDetail() {
  const { brandId, shopId, productId } = useParams<{
    brandId: string
    shopId: string
    productId: string
  }>()

  const listHref =
    brandId && shopId
      ? `/brands/${brandId}/integrations/printify?shopId=${encodeURIComponent(shopId)}`
      : '/brands/picker'

  const { data, isLoading, error } = useQuery({
    queryKey: ['printify', 'product', brandId, shopId, productId],
    queryFn: () => getPrintifyProduct(brandId!, shopId!, productId!),
    enabled: !!brandId && !!shopId && !!productId,
  })

  const product = data?.product as PrintifyProductDetail | undefined

  const orderedImages = useMemo(
    () => orderPrintifyProductImages(product?.images),
    [product?.images],
  )

  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  useEffect(() => {
    if (!orderedImages.length) {
      setSelectedImageIndex(0)
      return
    }
    const def = orderedImages.findIndex((img) => img.is_default)
    setSelectedImageIndex(def >= 0 ? def : 0)
  }, [product?.id, orderedImages])

  const mainImageSrc = orderedImages[selectedImageIndex]?.src

  const variants = Array.isArray(product?.variants) ? (product!.variants as Record<string, unknown>[]) : []

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Link
              to={listHref}
              className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-50 text-xl"
              aria-label="Back to Printify products"
            >
              ←
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 line-clamp-2">Printify product</h1>
              {shopId ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  Shop {shopId} · read-only
                </p>
              ) : null}
            </div>
          </div>
          {brandId ? (
            <Link
              to={`/brands/${brandId}/settings`}
              className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              aria-label="Settings"
            >
              ⚙️
            </Link>
          ) : null}
        </div>
      </header>

      <main className="p-4 space-y-4">
        {isLoading && <p className="text-sm text-gray-500">Loading product…</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
            {error instanceof Error ? error.message : 'Failed to load product'}
          </div>
        )}
        {!isLoading && !error && product && (
          <>
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200 aspect-[4/3] max-h-72 flex items-center justify-center">
                {mainImageSrc ? (
                  <img
                    src={mainImageSrc}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-400 text-sm">No image</span>
                )}
              </div>

              {orderedImages.length > 1 ? (
                <div
                  className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory"
                  role="tablist"
                  aria-label="Product images"
                >
                  {orderedImages.map((img, idx) => (
                    <button
                      key={`${img.src}-${idx}`}
                      type="button"
                      role="tab"
                      aria-selected={idx === selectedImageIndex}
                      aria-label={`Image ${idx + 1} of ${orderedImages.length}`}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`shrink-0 snap-start w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors touch-target-min-44 ${
                        idx === selectedImageIndex
                          ? 'border-emerald-600 ring-2 ring-emerald-200'
                          : 'border-gray-200 opacity-90 hover:opacity-100'
                      }`}
                    >
                      <img src={img.src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <section className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Summary</h2>
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium text-gray-500">Title</div>
                  <div className="text-base text-gray-900">{String(product.title ?? '—')}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Printify product id</div>
                  <div className="text-base font-mono text-gray-900">{String(product.id ?? productId)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Visible</div>
                  <div className="text-base text-gray-900">
                    {product.visible === false ? 'No (draft)' : 'Yes'}
                  </div>
                </div>
              </div>
            </section>

            {product.description ? (
              <section className="bg-white border border-gray-200 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h2>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(product.description)}</p>
              </section>
            ) : null}

            {variants.length > 0 && (
              <section className="bg-white border border-gray-200 rounded-lg p-4 overflow-x-auto">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Variants</h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                      <th className="pb-2 pr-2">Variant</th>
                      <th className="pb-2 pr-2">SKU</th>
                      <th className="pb-2">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => {
                      const id = v.id != null ? String(v.id) : Math.random().toString(36)
                      const title = v.title != null ? String(v.title) : '—'
                      const sku = v.sku != null ? String(v.sku) : '—'
                      const price = formatVariantPrice(v.price)
                      return (
                        <tr key={id} className="border-b border-gray-50 last:border-0">
                          <td className="py-2 pr-2 align-top">{title}</td>
                          <td className="py-2 pr-2 align-top font-mono text-xs">{sku}</td>
                          <td className="py-2 align-top">{price}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            )}

            <Link
              to={listHref}
              className="block w-full text-center bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium touch-target-min-44"
            >
              Back to products
            </Link>
          </>
        )}
      </main>
    </div>
  )
}

function formatVariantPrice(price: unknown): string {
  if (price == null) return '—'
  if (typeof price === 'number') {
    return (price / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
  }
  if (typeof price === 'object' && price !== null && 'amount' in price) {
    const amt = (price as { amount?: number }).amount
    if (typeof amt === 'number') {
      return (amt / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
    }
  }
  return String(price)
}
